import { Controller } from '../utils/controller';
import getPerlgemCSRFToken from '../utils/perlgem_csrf';

type SocialShoppeSetting = '1' | '0';

//INTERFACES
interface SalonData {
  SALON_ID?: string;
  NAV_LOGO: string;
  DOORNAME: string;
  LOOK_ID?: string;
  IS_ACOMMERCE_ELIGIBLE: 1 | 0;
}

interface SalonBackendParams {
  salon_id?: string;
  redirect?: string;
  socialshoppe?: SocialShoppeSetting;
  label?: string;
  artist_id?: string;
  logoImagePath?: string;
  force?: string;
  look_id?: string;
}

interface SessionStorageData {
  salonId?: string;
  logoImagePath: string;
  salonURL: string;
  socialShoppe: SocialShoppeSetting;
  salonName: string;
  artistID: string;
  label: string;
}

export default class extends Controller {
  static targets = ['salonLogo', 'salonName'];
  static requiredTargets = [];
  static values = {
    salonLogoAlt: {
      default: 'Salon Logo',
      type: String
    },
    backLinkRoot: {
      default: '',
      type: String
    },
    salonPageRoot: {
      default: '',
      type: String
    },
    salonLogoHeight: {
      default: '',
      type: String
    },
    salonLogoBgClass: {
      default: 'bg-none',
      type: String
    }
  };

  // these are stored in session storage either here or from PG. The casing needs to match.
  sessionStorageKeys = {
    salonId: 'SalonID',
    logoImagePath: 'logoImagePath',
    salonURL: 'salonURL',
    socialShoppe: 'socialShoppe',
    salonName: 'salonName',
    label: 'label',
    artistID: 'artistID'
  };

  // TARGETS

  declare readonly hasSalonLogoTarget: boolean;
  declare salonLogoTarget: HTMLDivElement;
  declare salonNameTarget: HTMLDivElement;

  // VALUES
  declare salonLogoAltValue: string;
  declare backLinkRootValue: string;
  declare salonPageRootValue: string;
  declare salonLogoHeightValue: string;
  declare salonLogoBgClassValue: string;
  declare salonLogoMarginRightValue: string;

  // LOCAL STATE
  private buildingStoreLogo: boolean;
  private defaultLogoImagePath: string;
  private siteDomain: string;

  initialize() {
    this.buildingStoreLogo = false;
    this.defaultLogoImagePath = '/media/images/profile/profile_full.jpg';
    this.siteDomain = window.location.hostname;
  }

  async connect() {
    const urlParams = this.getAllUrlParams();

    const salonSpecificParams = {
      salon_id: urlParams.get('salonid'),
      artist_id: urlParams.get('artistid'),
      socialshoppe: urlParams.get('socialshoppe'),
      label: urlParams.get('label'),
      force: urlParams.get('force'),
      redirect: urlParams.get('redirect'),
      look_id: urlParams.get('lookid'),
      products: urlParams.get('products')
    };

    const paramsWithoutEmptyValues = Object.entries(salonSpecificParams).reduce(
      (acc, [k, v]) => (v ? { ...acc, [k]: v } : acc),
      {}
    ) as SalonBackendParams;

    if (paramsWithoutEmptyValues['salon_id']) {
      this.getDataFromApiAndSaveInfo(paramsWithoutEmptyValues);
    } else {
      this.tryAndInsertStoreLogo();
    }

    //If products are present add them to cart
    if (paramsWithoutEmptyValues['products'] && paramsWithoutEmptyValues['salon_id']) {
      this.addProductsToCart(paramsWithoutEmptyValues['products']);
    }
  }

  getAllUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const caseInsensitiveUrlParams = new URLSearchParams();
    urlParams.forEach((value, key) => caseInsensitiveUrlParams.append(key.toLowerCase(), value));

    return caseInsensitiveUrlParams;
  }

  async addProductsToCart(products: string) {
    const skus = products.split(',');
    const token = await getPerlgemCSRFToken();

    if (!token) {
      return;
    }

    const params = {
      INCREMENT: 1,
      LAST_SOURCE: '/',
      QTY: 1,
      SKU_BASE_ID: skus,
      action: 'add',
      add_to_cart: 1,
      _TOKEN: token,
      _SUBMIT: 'cart'
    };

    const method = 'rpc.form';
    const addProductsFormData = this.stringifyFormData({ ...params }, method);
    const response = await this.submitJSONRPC(token, addProductsFormData, method);

    if (response) {
      // Remove products parameter after being added to cart
      const parameters = new URL(window.location.href);
      parameters.searchParams.delete('products');
      window.location.href = parameters.toString();
    }
  }

  async getDataFromApiAndSaveInfo(params) {
    const token = await getPerlgemCSRFToken();

    if (!token) {
      return;
    }

    const setCommissionsMethod = 'user.setSessionSalonCommissionData';
    const commissionsFormData = this.stringifyFormData(
      { ...params, _TOKEN: token },
      setCommissionsMethod
    );

    this.submitJSONRPC(token, commissionsFormData, setCommissionsMethod);

    const locatorGetNavDataMethod = 'locator.getNavDataByDoor';
    const doorsFormData = this.stringifyFormData(
      { ...params, doorId: null },
      locatorGetNavDataMethod
    );
    const [doorsResponse] = await this.submitJSONRPC(token, doorsFormData, locatorGetNavDataMethod);
    this.populateSessionStorage(doorsResponse.result.value, params);
  }

  buildLinkBackUrl(redirectUrlParam: string, salonID: string) {
    // fallback to use a salonID-based link if redirect was not passed in
    const linkBackUrl = redirectUrlParam || this.backLinkRootValue + salonID;

    const [baseUrl, searchParamString] = linkBackUrl.split('?');
    const queryParams = new URLSearchParams(searchParamString);

    if (queryParams.get('welcome') === 'true') {
      return linkBackUrl;
    }

    queryParams.set('welcome', 'true');

    return `${baseUrl}?${queryParams.toString()}`;
  }

  populateSessionStorage(salonData: SalonData, urlParams: SalonBackendParams) {
    if (!salonData) {
      return;
    }

    if (salonData.IS_ACOMMERCE_ELIGIBLE === 0) {
      Object.keys(this.sessionStorageKeys).forEach((key) => {
        sessionStorage.removeItem(key);
      });
      return;
    }

    const itemsToStoreToSession = {
      [this.sessionStorageKeys.salonId]: salonData.SALON_ID,
      [this.sessionStorageKeys.logoImagePath]:
        salonData.NAV_LOGO === this.defaultLogoImagePath ? '' : salonData.NAV_LOGO,
      [this.sessionStorageKeys.salonURL]: this.buildLinkBackUrl(
        urlParams.redirect,
        salonData.SALON_ID
      ),
      [this.sessionStorageKeys.socialShoppe]: urlParams.socialshoppe || '0',
      [this.sessionStorageKeys.salonName]: salonData.DOORNAME,
      [this.sessionStorageKeys.label]: urlParams.label,
      [this.sessionStorageKeys.artistID]: urlParams.artist_id
    };
    Object.entries(itemsToStoreToSession).forEach(([key, val]) => {
      val !== null && val !== undefined && sessionStorage.setItem(key, val);
    });

    this.tryAndInsertStoreLogo();
  }

  getSalonStoredData(): SessionStorageData {
    return {
      salonId: sessionStorage.getItem(this.sessionStorageKeys.salonId) || undefined,
      logoImagePath: sessionStorage.getItem(this.sessionStorageKeys.logoImagePath) || '',
      salonURL: sessionStorage.getItem(this.sessionStorageKeys.salonURL) || '',
      socialShoppe:
        (sessionStorage.getItem(this.sessionStorageKeys.socialShoppe) as SocialShoppeSetting) ||
        '0',
      salonName: sessionStorage.getItem(this.sessionStorageKeys.salonName) || '',
      artistID: sessionStorage.getItem(this.sessionStorageKeys.artistID) || '',
      label: sessionStorage.getItem(this.sessionStorageKeys.label) || ''
    };
  }

  tryAndInsertStoreLogo() {
    const storedData = this.getSalonStoredData();

    if (storedData?.salonId && !this.buildingStoreLogo && this.hasSalonLogoTarget) {
      this.buildingStoreLogo = true;

      const displayedSalonLogo = this.getSalonImagePath(storedData);
      const redirectURL = this.getRedirectURL(storedData);
      const backToOrigin = this.getBackToOriginNavigation();
      const optionalHeightValue = this.salonLogoHeightValue
        ? `h-[${this.salonLogoHeightValue}]`
        : '';

      const generateElementForPlatform = () => `
        <a href="${redirectURL}" title="${storedData.salonName}" class="flex items-center">
          <img 
            src="${displayedSalonLogo}" 
            onerror="if (!this.src.includes('profile_full.jpg')) this.src='${this.defaultLogoImagePath}'" 
            alt="${this.salonLogoAltValue}" 
            class="header-salon-logo p-px ${this.salonLogoBgClassValue} ${optionalHeightValue}"
            loading="lazy"
          />  
          ${backToOrigin}
        </a>
      `;

      // Desktop logo has its own target
      this.salonLogoTarget.innerHTML += generateElementForPlatform();
      const desktopLogoElement = this.element.querySelector('.desktop-header-brand-logo');
      if (desktopLogoElement) {
        desktopLogoElement.classList.add('desktop-header-logo-separator');
      }
      // The Mobile Gnav uses the Vulcan Blocks framework, where the brand logo is rendered
      const mobileLogoElement = this.element.querySelector('.utility-nav-logo');
      if (mobileLogoElement) {
        mobileLogoElement.innerHTML = generateElementForPlatform();
      }

      if (storedData.socialShoppe === '1') {
        this.salonNameTarget.innerHTML += `<a href="${redirectURL}">< ${backToOrigin}</a>`;
      }
    }
  }

  getBackToOriginNavigation() {
    const storedData = this.getSalonStoredData();

    if (storedData.socialShoppe === '1') {
      if (storedData.artistID && storedData.label) {
        return `Back to ${storedData.label}`;
      } else {
        return `Back to ${storedData.salonName}`;
      }
    }

    return '';
  }

  getSalonImagePath(storedData: SessionStorageData) {
    if (storedData.socialShoppe === '1') {
      const isProd = this.siteDomain.match(/^www\.aveda\.\D+/gi);
      const environment = isProd ? `hausmartprod` : `hausmartstaging`;
      return `https://res.cloudinary.com/${environment}/image/upload/salon-logo/${storedData.salonId}.png`;
    } else if (storedData.logoImagePath) {
      return `https://${this.siteDomain}${storedData.logoImagePath}`;
    }

    return `https://${this.siteDomain}${this.defaultLogoImagePath}`;
  }

  getRedirectURL({
    salonId,
    socialShoppe,
    salonURL
  }: {
    salonId?: string;
    socialShoppe: SocialShoppeSetting;
    salonURL: string;
  }) {
    const getTheFactsURL = `/locator/get_the_facts.tmpl?SalonID=${salonId}`;

    if (socialShoppe === '1' && salonURL) {
      const validURL = this.sanitizeSalonURL(salonURL);
      return validURL ? `https://${validURL}` : getTheFactsURL;
    }

    return getTheFactsURL;
  }

  sanitizeSalonURL(salonURL) {
    const baseURL = this.salonPageRootValue;
    const baseRegexPattern = baseURL.replace(/\./g, '\\.');
    const regexPattern = new RegExp(`^${baseRegexPattern}/(salon|a|from-link)/.+`);

    return regexPattern.test(salonURL.replaceAll('%2f', '/'))
      ? salonURL.replaceAll('%2f', '/')
      : false;
  }

  stringifyFormData(data: Record<string, unknown>, method: string) {
    const formData = new URLSearchParams();
    formData.append(
      'JSONRPC',
      JSON.stringify([
        {
          method,
          params: [{ ...data }]
        }
      ])
    );
    return formData;
  }

  async submitJSONRPC(token: string, formData: BodyInit, method: string) {
    return await fetch(`/rpc/jsonrpc.tmpl?dbgmethod=${method}`, {
      method: 'POST',
      body: formData,
      mode: 'cors',
      headers: {
        'x-csrf-token': token
      }
    })
      .then(async (res) => await res.json())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }
}
